import { useState } from 'react'
import { AuthenticatedImage } from '@/components/features/authenticated-image'
import { ImageLightboxDialog } from '@/components/features/image-lightbox-dialog'
import type { DocumentRendererProps } from '@/lib/document-renderer-registry'
import {
  sessionImageCaptionFromDocument,
  sessionImageSrcFromDocument,
} from '@/lib/session-image-card'
import { testIds } from '@/lib/test-ids'

/** Preview player for kind: session_image - shows the attachment, not just the card text. */
export function SessionImageDocumentRenderer({ document }: DocumentRendererProps) {
  const src = sessionImageSrcFromDocument(document)
  const caption = sessionImageCaptionFromDocument(document)
  const [lightboxOpen, setLightboxOpen] = useState(false)

  if (!src) {
    return (
      <div
        className="flex h-full items-center justify-center p-6 text-sm text-muted-foreground"
        data-testid={testIds.sessionImage.viewer}
      >
        This session image card has no attachment pointer.
      </div>
    )
  }

  return (
    <div
      className="flex h-full min-h-0 flex-col overflow-auto p-6"
      data-testid={testIds.sessionImage.viewer}
    >
      <button
        type="button"
        data-testid={testIds.sessionImage.thumb}
        title="Click to enlarge"
        aria-label="Open image preview"
        className="mx-auto max-w-full cursor-zoom-in rounded-md border border-border bg-muted/30 p-2 outline-none focus-visible:ring-2 focus-visible:ring-ring"
        onClick={() => setLightboxOpen(true)}
      >
        <AuthenticatedImage
          rawSrc={src}
          alt={caption || 'Session image'}
          className="mx-auto block max-h-[calc(100dvh-12rem)] w-auto max-w-full object-contain"
        />
      </button>
      {caption ? (
        <p
          data-testid={testIds.sessionImage.caption}
          className="mx-auto mt-3 max-w-prose text-center text-sm text-muted-foreground"
        >
          {caption}
        </p>
      ) : null}
      <ImageLightboxDialog
        src={lightboxOpen ? src : null}
        altCaption={caption}
        onClose={() => setLightboxOpen(false)}
        testId={testIds.sessionImage.lightbox}
        captionTestId={testIds.sessionImage.lightboxCaption}
      />
    </div>
  )
}
